import {Deferred, noop} from '@sail/utils';

import type {FetchResult} from '@apollo/client';

type CacheItem = {
  promise: Promise<FetchResult>;
  result: FetchResult | undefined;
  queue: Array<() => void>;
};

type CacheDeferred = {
  promise: Promise<FetchResult>;
  resolve: (result: FetchResult) => void;
  reject: (error: any) => void;
};

export const defaultExpireAfterMs = 60 * 1000;

export default class PrefetchCache {
  private _items: Map<string, CacheItem> = new Map();
  private _deferreds: Map<string, CacheDeferred> = new Map();
  private _expireAfterMs: number;

  constructor(options?: {expireAfterMs?: number}) {
    this._expireAfterMs = options?.expireAfterMs ?? defaultExpireAfterMs;
  }

  addItem(hash: string, pruningSignal?: AbortSignal): CacheItem {
    const existingItem = this._items.get(hash);
    const existingDeferred = this._deferreds.get(hash);

    const prune = (): void => {
      pruningSignal?.removeEventListener('abort', prune);
      const item = this._items.get(hash);

      if (!item) {
        this._deferreds.delete(hash);
        return;
      }

      const selfIndex = item.queue.findIndex((el) => el === prune);

      if (selfIndex === -1) {
        return;
      }

      item.queue.splice(selfIndex, 1);

      if (item.queue.length === 0) {
        this._items.delete(hash);

        if (item.result !== undefined) {
          // Only remove the underlying deferred if the promise was resolved.
          this._deferreds.delete(hash);
        }
      }
    };

    if (existingItem && existingDeferred) {
      existingItem.queue.push(prune);
      pruningSignal?.addEventListener('abort', prune);

      return existingItem;
    }

    const deferred = new Deferred<FetchResult>();

    deferred.promise.catch(noop);

    const queue = [];

    queue.push(prune);

    const deferredItem: CacheDeferred = existingDeferred ?? {
      promise: deferred.promise,

      resolve: (result: FetchResult): void => {
        if (cacheItem.result !== undefined) {
          return;
        }

        cacheItem.result = result;
        deferred.resolve(result);

        if (cacheItem.queue.length === 0) {
          // If the queue is empty at the time the promise is resolved,
          // we can remove the underlying deferred.
          this._deferreds.delete(hash);
        }

        if (pruningSignal?.aborted) {
          prune();
        } else {
          pruningSignal?.addEventListener('abort', () => {
            prune();
          });
        }

        setTimeout(() => {
          // Unconditionally remove the item from the cache
          // after the expiration time has passed, instead
          // of simply calling `prune()` (i.e. popping an element
          // out of the queue) to ensure possibly stale values
          // don't live in the cache for too long.
          this._items.delete(hash);
          this._deferreds.delete(hash);
        }, this._expireAfterMs);
      },

      reject: (error: any): void => {
        deferred.reject(error);

        this._items.delete(hash);
        this._deferreds.delete(hash);
      },
    };

    const cacheItem: CacheItem = {
      promise: deferredItem.promise,
      result: undefined,
      queue,
    };

    this._items.set(hash, cacheItem);
    this._deferreds.set(hash, deferredItem);

    return cacheItem;
  }

  resolve(hash: string, result: FetchResult): void {
    return this._deferreds.get(hash)?.resolve(result);
  }

  reject(hash: string, error: any): void {
    return this._deferreds.get(hash)?.reject(error);
  }

  // Removes an entry from the cache without settling its deferred. Used to drop
  // a cancelled in-flight prefetch: unlike `reject`, it does not reject the
  // shared promise, so a cancellation never surfaces an error on the observable
  evict(hash: string): void {
    this._items.delete(hash);
    this._deferreds.delete(hash);
  }

  getItem(hash: string): FetchResult | Promise<FetchResult> | undefined {
    const existingItem = this._items.get(hash);

    if (!existingItem) {
      return undefined;
    }

    const pruneFn = existingItem.queue[0];
    pruneFn?.();

    return existingItem.result ?? existingItem.promise;
  }

  clear(): void {
    this._items.clear();
  }
}
